
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const AdminBrochures = () => {
  const [brochures, setBrochures] = useState([]);
  const [pdfName, setPdfName] = useState('');
  const [pdfDescription, setPdfDescription] = useState('');
  const [image, setImage] = useState('');
  const [pdf, setPdf] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'manage'

  useEffect(() => {
    fetchBrochures();
  }, []);

  const fetchBrochures = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_REQUESTING_API}/api/brochures`
      );
      setBrochures(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to fetch brochures:', error);
    }
  };

  // Convert image to base64
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadStatus('❌ Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
    };
    reader.onerror = () => {
      setUploadStatus('❌ Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  // Convert PDF to base64
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setUploadStatus('❌ Please select a valid PDF file');
      return;
    }

    // Check file size (16MB limit for MongoDB)
    if (file.size > 15 * 1024 * 1024) {
      setUploadStatus('❌ PDF file is too large. Maximum size is 15MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPdf(reader.result);
    };
    reader.onerror = () => {
      setUploadStatus('❌ Failed to read PDF file');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadStatus('');

    try {
      if (!pdfName || !pdfDescription || !image || !pdf) {
        setUploadStatus('❌ All fields are required');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_REQUESTING_API}/api/brochures`,
        {
          pdfName,
          pdfDescription,
          image,
          pdf,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setUploadStatus('✅ Brochure uploaded successfully!');
        // Clear form
        setPdfName('');
        setPdfDescription('');
        setImage('');
        setPdf('');
        document.getElementById('imageInput').value = '';
        document.getElementById('pdfInput').value = '';
        fetchBrochures();
        
        // Auto switch to manage tab after 2 seconds
        setTimeout(() => {
          setActiveTab('manage');
          setUploadStatus('');
        }, 2000);
      } else {
        setUploadStatus('❌ Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus(
        `❌ ${err.response?.data?.message || 'Upload failed. Please try again.'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brochure?')) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_REQUESTING_API}/api/brochures/${id}`
      );

      if (response.data.success) {
        setUploadStatus('✅ Brochure deleted successfully!');
        fetchBrochures();
        setTimeout(() => setUploadStatus(''), 3000);
      }
    } catch (err) {
      console.error('Delete error:', err);
      setUploadStatus('❌ Failed to delete brochure');
      setTimeout(() => setUploadStatus(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-3">
            Brochure Management
          </h1>
          <p className="text-gray-600 text-lg">
            Upload and manage your organization's brochures
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-2xl shadow-lg p-2 gap-2">
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-8 py-3 rounded-xl font-bold transition-all duration-300 ${
                activeTab === 'upload'
                  ? 'bg-gray-800 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload New
              </span>
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`px-8 py-3 rounded-xl font-bold transition-all duration-300 ${
                activeTab === 'manage'
                  ? 'bg-gray-800 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Manage ({brochures.length})
              </span>
            </button>
          </div>
        </div>

        {/* Upload Status Message */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`max-w-3xl mx-auto mb-6 p-4 rounded-xl font-semibold text-center ${
              uploadStatus.includes('✅')
                ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-red-100 text-red-800 border-2 border-red-300'
            }`}
          >
            {uploadStatus}
          </motion.div>
        )}

        {/* Content Based on Active Tab */}
        {activeTab === 'upload' ? (
          // UPLOAD SECTION
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
                  <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Upload New Brochure</h2>
                    <p className="text-gray-600 text-sm">Fill in the details below</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* PDF Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Brochure Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Organization Overview 2024"
                      value={pdfName}
                      onChange={(e) => setPdfName(e.target.value)}
                      required
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      placeholder="Brief description of the brochure content..."
                      value={pdfDescription}
                      onChange={(e) => setPdfDescription(e.target.value)}
                      required
                      rows="4"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all resize-none"
                    />
                  </div>

                  {/* Preview Image */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Preview Image * (JPG/PNG)
                    </label>
                    <div className="relative">
                      <input
                        id="imageInput"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        required
                        className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                      />
                    </div>
                    {image && (
                      <div className="mt-4 flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                        <img
                          src={image}
                          alt="Preview"
                          className="w-24 h-24 object-cover rounded-lg shadow-md"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-700">✓ Image loaded successfully</p>
                          <p className="text-xs text-gray-500 mt-1">Preview will be shown on brochure card</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PDF File */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      PDF File * (Max 15MB)
                    </label>
                    <div className="relative">
                      <input
                        id="pdfInput"
                        type="file"
                        accept="application/pdf"
                        onChange={handlePdfChange}
                        required
                        className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                      />
                    </div>
                    {pdf && (
                      <div className="mt-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="flex items-center gap-3">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-green-800">✓ PDF loaded successfully</p>
                            <p className="text-xs text-green-600 mt-1">Ready to upload</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload Brochure
                      </span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        ) : (
          // MANAGE SECTION
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">Manage Brochures</h2>
                      <p className="text-gray-600 text-sm">View and delete existing brochures</p>
                    </div>
                  </div>
                  <div className="bg-gray-800 text-white px-4 py-2 rounded-xl font-bold">
                    {brochures.length} Total
                  </div>
                </div>

                {brochures.length === 0 ? (
                  <div className="text-center py-16">
                    <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-xl font-bold text-gray-400 mb-2">No brochures uploaded yet</h3>
                    <p className="text-gray-500 mb-6">Start by uploading your first brochure</p>
                    <button
                      onClick={() => setActiveTab('upload')}
                      className="inline-flex items-center gap-2 bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-900 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Upload Brochure
                    </button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {brochures.map((b) => (
                      <motion.div
                        key={b._id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-5 border-2 border-gray-100 hover:border-gray-300"
                      >
                        <div className="flex gap-4">
                          <img
                            src={b.image}
                            alt={b.pdfName}
                            className="w-28 h-28 object-cover rounded-lg shadow-md flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-800 text-lg mb-2 line-clamp-1">
                              {b.pdfName}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                              {b.pdfDescription}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {new Date(b.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(b._id)}
                          className="w-52 mt-4 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 rounded-lg font-bold hover:bg-red-700 transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete Brochure
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminBrochures;

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminBrochures = () => {
//   const [brochures, setBrochures] = useState([]);
//   const [pdfName, setPdfName] = useState('');
//   const [pdfDescription, setPdfDescription] = useState('');
//   const [image, setImage] = useState('');
//   const [pdf, setPdf] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [uploadStatus, setUploadStatus] = useState(''); // Success/Error message

//   useEffect(() => {
//     fetchBrochures();
//   }, []);

//   const fetchBrochures = async () => {
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_REQUESTING_API}/api/brochures`
//       );
//       setBrochures(res.data.data || res.data); // Handle both response formats
//     } catch (error) {
//       console.error('Failed to fetch brochures:', error);
//     }
//   };

//   // Convert image to base64
//   const handleImageChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     if (!file.type.startsWith('image/')) {
//       setUploadStatus('❌ Please select a valid image file');
//       return;
//     }

//     const reader = new FileReader();
//     reader.onloadend = () => {
//       setImage(reader.result); // base64 string
//     };
//     reader.onerror = () => {
//       setUploadStatus('❌ Failed to read image file');
//     };
//     reader.readAsDataURL(file);
//   };

//   // Convert PDF to base64
//   const handlePdfChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     if (file.type !== 'application/pdf') {
//       setUploadStatus('❌ Please select a valid PDF file');
//       return;
//     }

//     const reader = new FileReader();
//     reader.onloadend = () => {
//       setPdf(reader.result); // base64 string
//     };
//     reader.onerror = () => {
//       setUploadStatus('❌ Failed to read PDF file');
//     };
//     reader.readAsDataURL(file);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setUploadStatus('');

//     try {
//       // Validation
//       if (!pdfName || !pdfDescription || !image || !pdf) {
//         setUploadStatus('❌ All fields are required');
//         setLoading(false);
//         return;
//       }

//       // Send as JSON (not FormData) because we're using base64
//       const response = await axios.post(
//         `${process.env.REACT_APP_REQUESTING_API}/api/brochures`,
//         {
//           pdfName,
//           pdfDescription,
//           image, // base64
//           pdf,   // base64
//         },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       if (response.data.success) {
//         setUploadStatus('✅ Brochure uploaded successfully!');
//         // Clear form
//         setPdfName('');
//         setPdfDescription('');
//         setImage('');
//         setPdf('');
//         // Clear file inputs
//         document.getElementById('imageInput').value = '';
//         document.getElementById('pdfInput').value = '';
//         // Refresh list
//         fetchBrochures();
//       } else {
//         setUploadStatus('❌ Upload failed');
//       }
//     } catch (err) {
//       console.error('Upload error:', err);
//       setUploadStatus(
//         `❌ ${err.response?.data?.message || 'Upload failed. Please try again.'}`
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm('Delete this brochure?')) return;

//     try {
//       const response = await axios.delete(
//         `${process.env.REACT_APP_REQUESTING_API}/api/brochures/${id}`
//       );
      
//       if (response.data.success) {
//         setUploadStatus('✅ Brochure deleted successfully!');
//         fetchBrochures();
//       }
//     } catch (err) {
//       console.error('Delete error:', err);
//       setUploadStatus('❌ Failed to delete brochure');
//     }
//   };

//   return (
//     <div className="max-w-5xl mx-auto py-12 px-6">
//       <h1 className="text-3xl font-black mb-8">Manage Brochures</h1>

//       {/* Upload Status Message */}
//       {uploadStatus && (
//         <div
//           className={`mb-6 p-4 rounded-xl font-semibold ${
//             uploadStatus.includes('✅')
//               ? 'bg-green-100 text-green-800'
//               : 'bg-red-100 text-red-800'
//           }`}
//         >
//           {uploadStatus}
//         </div>
//       )}

//       {/* Upload Form */}
//       <form
//         onSubmit={handleSubmit}
//         className="bg-white p-6 rounded-xl shadow mb-12 space-y-4"
//       >
//         <div>
//           <label className="block text-sm font-bold mb-2">Brochure Name *</label>
//           <input
//             type="text"
//             placeholder="e.g., Company Brochure 2024"
//             value={pdfName}
//             onChange={(e) => setPdfName(e.target.value)}
//             required
//             className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-800"
//           />
//         </div>

//         <div>
//           <label className="block text-sm font-bold mb-2">Description *</label>
//           <textarea
//             placeholder="Brief description of the brochure"
//             value={pdfDescription}
//             onChange={(e) => setPdfDescription(e.target.value)}
//             required
//             rows="3"
//             className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-800"
//           />
//         </div>

//         <div>
//           <label className="block text-sm font-bold mb-2">
//             Preview Image * (JPG/PNG)
//           </label>
//           <input
//             id="imageInput"
//             type="file"
//             accept="image/*"
//             onChange={handleImageChange}
//             required
//             className="w-full p-3 border rounded-lg"
//           />
//           {image && (
//             <img
//               src={image}
//               alt="Preview"
//               className="mt-2 w-32 h-32 object-cover rounded-lg"
//             />
//           )}
//         </div>

//         <div>
//           <label className="block text-sm font-bold mb-2">PDF File *</label>
//           <input
//             id="pdfInput"
//             type="file"
//             accept="application/pdf"
//             onChange={handlePdfChange}
//             required
//             className="w-full p-3 border rounded-lg"
//           />
//           {pdf && (
//             <p className="mt-2 text-sm text-green-600 font-semibold">
//               ✓ PDF loaded
//             </p>
//           )}
//         </div>

//         <button
//           type="submit"
//           disabled={loading}
//           className="bg-gray-800 text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed w-full"
//         >
//           {loading ? 'Uploading...' : 'Add Brochure'}
//         </button>
//       </form>

//       {/* Brochure List */}
//       <h2 className="text-2xl font-bold mb-6">Uploaded Brochures</h2>
//       <div className="grid md:grid-cols-2 gap-6">
//         {brochures.length === 0 ? (
//           <p className="text-gray-500 col-span-2 text-center py-8">
//             No brochures uploaded yet
//           </p>
//         ) : (
//           brochures.map((b) => (
//             <div
//               key={b._id}
//               className="bg-white rounded-xl shadow p-4 flex gap-4"
//             >
//               <img
//                 src={b.image}
//                 alt={b.pdfName}
//                 className="w-24 h-24 object-cover rounded"
//               />
//               <div className="flex-grow">
//                 <h3 className="font-bold">{b.pdfName}</h3>
//                 <p className="text-sm text-gray-600 line-clamp-2">
//                   {b.pdfDescription}
//                 </p>
//               </div>
//               <button
//                 onClick={() => handleDelete(b._id)}
//                 className="text-red-600 font-bold hover:text-red-800 px-3"
//               >
//                 Delete
//               </button>
//             </div>
//           ))
//         )}
//       </div>
//     </div>
//   );
// };

// export default AdminBrochures;